/**
 * TUS Upload Service for resumable, chunk-based uploads
 * 
 * Uses tus-js-client configured for Supabase Storage
 * - 6MB chunks for memory efficiency
 * - Exponential backoff retry
 * - Auto-resume from previous uploads via TUS fingerprinting
 */

import * as tus from 'tus-js-client';
import { supabase } from '@/integrations/supabase/client';

const SUPABASE_PROJECT_REF = 'sekiwtiutnikmdbpiufi';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNla2l3dGl1dG5pa21kYnBpdWZpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzUzOTI0MzIsImV4cCI6MjA1MDk2ODQzMn0.OYCXJ_wPnVWdgSeTHGc_fHaHP5pHhsGBt1TN2zeLz0M';
const TUS_ENDPOINT = `https://${SUPABASE_PROJECT_REF}.supabase.co/storage/v1/upload/resumable`;

// 6MB chunk size - balances memory vs request overhead
const CHUNK_SIZE = 6 * 1024 * 1024;

// Retry delays with exponential backoff (ms)
const RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 20000];

// Stall detection - abort if no progress for this duration
const STALL_TIMEOUT_MS = 60000; // 60 seconds

// First progress timeout - fail fast if no progress at all
const FIRST_PROGRESS_TIMEOUT_MS = 30000; // 30 seconds to see ANY progress

// Completion timeout - if at 100% but no onSuccess, assume success
const COMPLETION_TIMEOUT_MS = 30000; // 30 seconds after 100%

// Token refresh threshold - refresh if expiring within this time
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export interface TusUploadOptions {
  blob: Blob;
  bucketName: string;
  filePath: string;
  contentType: string;
  onProgress?: (progress: number, bytesUploaded: number, bytesTotal: number) => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  abortSignal?: AbortSignal;
}

export interface TusUploadResult {
  success: boolean;
  filePath?: string;
  error?: Error;
}

/**
 * Get current auth token, refreshing if needed or expiring soon
 */
async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    console.log('[TUS] No session, attempting refresh...');
    const { data: refreshed } = await supabase.auth.refreshSession();
    return refreshed.session?.access_token || null;
  }
  
  // Check if token is expiring soon (within 5 minutes)
  const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
  const timeUntilExpiry = expiresAt - Date.now();
  
  if (timeUntilExpiry < TOKEN_REFRESH_THRESHOLD_MS) {
    console.log('[TUS] Token expiring soon, refreshing...', { timeUntilExpiry: Math.round(timeUntilExpiry / 1000) + 's' });
    const { data: refreshed } = await supabase.auth.refreshSession();
    return refreshed.session?.access_token || session.access_token;
  }
  
  return session.access_token;
}

/**
 * Pre-flight check to verify TUS endpoint is reachable
 * Fails fast if endpoint is unreachable or auth is invalid
 */
async function checkEndpointConnectivity(token: string): Promise<{ reachable: boolean; error?: string }> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    const response = await fetch(TUS_ENDPOINT, {
      method: 'OPTIONS',
      headers: {
        authorization: `Bearer ${token}`,
        apikey: SUPABASE_ANON_KEY,
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (response.ok || response.status === 204) {
      console.log('[TUS] Pre-flight check passed');
      return { reachable: true };
    }
    
    if (response.status === 401 || response.status === 403) {
      return { reachable: false, error: 'Authentication failed - please refresh page' };
    }
    
    return { reachable: false, error: `Endpoint returned ${response.status}` };
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      return { reachable: false, error: 'Connection timeout - check your internet' };
    }
    return { reachable: false, error: 'Network unreachable' };
  }
}

/**
 * Upload a blob using TUS protocol for resumable uploads
 * Returns the file path on success
 */
export function uploadWithTus(options: TusUploadOptions): Promise<TusUploadResult> {
  const { blob, bucketName, filePath, contentType, onProgress, onError, onSuccess } = options;

  return new Promise(async (resolve) => {
    // 1. Get fresh token (refreshes if expiring soon)
    const token = await getAuthToken();
    
    if (!token) {
      const error = new Error('No valid auth session');
      onError?.(error);
      resolve({ success: false, error });
      return;
    }

    // 2. Pre-flight connectivity check - fail fast if unreachable
    const connectivity = await checkEndpointConnectivity(token);
    if (!connectivity.reachable) {
      const error = new Error(connectivity.error || 'Upload endpoint unreachable');
      console.error('[TUS] Pre-flight check failed:', connectivity.error);
      onError?.(error);
      resolve({ success: false, error });
      return;
    }

    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    let firstProgressTimer: ReturnType<typeof setTimeout> | null = null;
    let completionTimer: ReturnType<typeof setTimeout> | null = null;
    let isResolved = false;
    let lastProgress = 0;
    let hasReceivedFirstProgress = false;

    const resetStallTimer = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        if (!isResolved) {
          console.error('[TUS] Upload stalled - no progress for 60 seconds:', filePath);
          upload.abort();
          const error = new Error('Upload stalled - no progress received');
          onError?.(error);
          isResolved = true;
          resolve({ success: false, error });
        }
      }, STALL_TIMEOUT_MS);
    };

    const clearStallTimer = () => {
      if (stallTimer) {
        clearTimeout(stallTimer);
        stallTimer = null;
      }
    };

    const clearFirstProgressTimer = () => {
      if (firstProgressTimer) {
        clearTimeout(firstProgressTimer);
        firstProgressTimer = null;
      }
    };

    const clearCompletionTimer = () => {
      if (completionTimer) {
        clearTimeout(completionTimer);
        completionTimer = null;
      }
    };

    const clearAllTimers = () => {
      clearStallTimer();
      clearFirstProgressTimer();
      clearCompletionTimer();
    };

    const upload = new tus.Upload(blob, {
      endpoint: TUS_ENDPOINT,
      retryDelays: RETRY_DELAYS,
      chunkSize: CHUNK_SIZE,
      headers: {
        authorization: `Bearer ${token}`,
        apikey: SUPABASE_ANON_KEY,
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName,
        objectName: filePath,
        contentType,
        cacheControl: '3600',
      },
      onError: (error) => {
        clearAllTimers();
        if (!isResolved) {
          console.error('[TUS] Upload error:', error);
          onError?.(error);
          isResolved = true;
          resolve({ success: false, error });
        }
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
        
        // Clear first progress timer on ANY progress
        if (!hasReceivedFirstProgress && bytesUploaded > 0) {
          hasReceivedFirstProgress = true;
          clearFirstProgressTimer();
          console.log('[TUS] First progress received - upload is transmitting:', filePath);
        }
        
        // Only reset stall timer if ACTUAL progress was made
        if (percentage > lastProgress) {
          console.log(`[TUS] Progress: ${lastProgress}% → ${percentage}%`, filePath);
          lastProgress = percentage;
          
          // Reset stall timer only when bytes are actually moving (and not at 100%)
          if (percentage < 100) {
            resetStallTimer();
          }
        }
        
        // Handle 100% completion timeout
        if (percentage >= 100 && !completionTimer && !isResolved) {
          console.log('[TUS] Upload at 100%, waiting for server confirmation...', filePath);
          clearStallTimer();
          
          completionTimer = setTimeout(() => {
            if (!isResolved) {
              console.warn('[TUS] No onSuccess after 100% - treating as complete:', filePath);
              onSuccess?.();
              isResolved = true;
              resolve({ success: true, filePath });
            }
          }, COMPLETION_TIMEOUT_MS);
        }
        
        onProgress?.(percentage, bytesUploaded, bytesTotal);
      },
      onSuccess: () => {
        clearAllTimers();
        if (!isResolved) {
          console.log('[TUS] Upload complete:', filePath);
          onSuccess?.();
          isResolved = true;
          resolve({ success: true, filePath });
        }
      },
    });

    // Check for previous partial uploads (resumability!)
    try {
      const previousUploads = await upload.findPreviousUploads();
      if (previousUploads.length > 0) {
        console.log('[TUS] Resuming previous upload for:', filePath);
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }
    } catch (e) {
      // Fingerprint lookup failed, start fresh
      console.log('[TUS] No previous upload found, starting fresh');
    }

    // Log upload start for debugging
    console.log(`[TUS] Starting upload: ${filePath}, size: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
    
    // 3. Start aggressive first-progress timeout (30s) - detects stuck uploads fast
    firstProgressTimer = setTimeout(() => {
      if (!hasReceivedFirstProgress && !isResolved) {
        console.error('[TUS] No initial progress in 30s - upload may be blocked:', filePath);
        upload.abort();
        const error = new Error('Upload failed to start - check your connection');
        onError?.(error);
        isResolved = true;
        resolve({ success: false, error });
      }
    }, FIRST_PROGRESS_TIMEOUT_MS);
    
    // Start stall detection timer (for after first progress)
    resetStallTimer();

    // Handle external abort signal
    if (options.abortSignal) {
      options.abortSignal.addEventListener('abort', () => {
        if (!isResolved) {
          console.log('[TUS] Upload aborted via signal:', filePath);
          clearAllTimers();
          upload.abort();
          const error = new Error('Upload aborted');
          onError?.(error);
          isResolved = true;
          resolve({ success: false, error });
        }
      });
    }

    upload.start();
  });
}

/**
 * Abort a TUS upload
 */
export function abortTusUpload(upload: tus.Upload): void {
  try {
    upload.abort();
    console.log('[TUS] Upload aborted');
  } catch (e) {
    console.warn('[TUS] Error aborting upload:', e);
  }
}

/**
 * Calculate estimated upload time based on file size and connection
 */
export function estimateUploadTime(fileSize: number, connectionSpeedMbps: number = 5): number {
  // Convert Mbps to bytes per second
  const bytesPerSecond = (connectionSpeedMbps * 1000 * 1000) / 8;
  return Math.ceil(fileSize / bytesPerSecond);
}
